/**
 * DraftRecoveryModal - HIPAA-Compliant Draft Recovery Prompt
 * 
 * Shown when the app detects an encrypted draft in sessionStorage
 * that differs from the server version. Allows user to choose
 * between their local changes or the server version.
 */

import React from 'react';
import { AlertTriangle, Clock, FileText, RefreshCw, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';

/**
 * Format the draft timestamp for display
 */
const formatDraftTime = (timestamp) => {
  if (!timestamp) return 'Unknown time';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHr = Math.floor(diffMin / 60);
    
    let relativeTime;
    if (diffMin < 1) relativeTime = 'just now';
    else if (diffMin < 60) relativeTime = `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    else if (diffHr < 24) relativeTime = `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
    else relativeTime = date.toLocaleDateString();
    
    const timeStr = date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    return `${relativeTime} (${timeStr})`;
  } catch (e) {
    return 'Unknown time';
  }
};

/**
 * Count the number of fields with data in the draft
 */
const countDraftFields = (formData) => {
  if (!formData || typeof formData !== 'object') return 0;
  
  return Object.values(formData).filter(value => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  }).length;
};

/**
 * Get summary of draft contents for display
 */
const getDraftSummary = (draft) => {
  const summary = {
    fieldCount: countDraftFields(draft?.formData),
    hasConsent: false,
    goalsCount: 0,
  };
  
  // Check for consent data
  if (draft?.consentData) {
    const consent = draft.consentData;
    summary.hasConsent = !!(consent.provider_location || consent.pos_code || consent.modality);
  }
  
  // Check for linked goals
  if (draft?.linkedGoals && Array.isArray(draft.linkedGoals)) {
    summary.goalsCount = draft.linkedGoals.length;
  }
  
  // Also check fieldCounts if available (from newer drafts)
  if (draft?.fieldCounts) {
    summary.hasConsent = summary.hasConsent || draft.fieldCounts.hasConsent;
    summary.goalsCount = summary.goalsCount || draft.fieldCounts.goalsCount;
  }
  
  return summary;
};

export function DraftRecoveryModal({
  isOpen,
  onRestore,
  onDismiss,
  draft,
}) {
  if (!isOpen || !draft) return null;
  
  const draftSummary = getDraftSummary(draft);
  const draftTime = formatDraftTime(draft.timestamp);
  
  // Get client name from draft metadata
  const clientName = draft.clientName || null;
  
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onDismiss()}>
      <AlertDialogContent className="max-w-md border-2 border-orange-300">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 animate-pulse">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
            </div>
            <AlertDialogTitle className="text-lg text-orange-900">
              Restore Unsaved Changes?
            </AlertDialogTitle>
          </div>
          
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-gray-600">
              {/* CLIENT NAME CONFIRMATION - CRITICAL FOR PROVIDER CONFIDENCE */}
              {clientName && (
                <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 text-orange-900">
                  <p className="font-semibold">
                    Found unsaved changes for:
                  </p>
                  <p className="text-lg font-bold mt-1">{clientName}</p>
                </div>
              )}
              
              <p>
                We found unsaved changes from a previous session that weren't 
                saved to the server. This can happen if your connection was 
                interrupted or the browser closed unexpectedly.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Last edited:</span>
                  <span>{draftTime}</span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-700">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Form fields:</span>
                  <span>{draftSummary.fieldCount} fields</span>
                </div>
                
                {draftSummary.hasConsent && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="h-4 w-4 text-gray-500">✓</span>
                    <span className="font-medium">Consent selections saved</span>
                  </div>
                )}
                
                {draftSummary.goalsCount > 0 && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="h-4 w-4 text-gray-500">🎯</span>
                    <span className="font-medium">Treatment goals:</span>
                    <span>{draftSummary.goalsCount} goal{draftSummary.goalsCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
              
              <p className="text-orange-700 bg-orange-50 p-2 rounded text-xs border border-orange-200">
                <strong>⚠️ Warning:</strong> Choosing "Use Server Version" will 
                permanently discard your local changes.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel asChild>
            <Button
              variant="outline"
              onClick={onDismiss}
              className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <X className="h-4 w-4" />
              Use Server Version
            </Button>
          </AlertDialogCancel>
          
          <AlertDialogAction asChild>
            <Button
              onClick={onRestore}
              className="gap-2 bg-orange-600 hover:bg-orange-700"
            >
              <RefreshCw className="h-4 w-4" />
              Restore My Changes
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Inline Draft Recovery Banner - HIGH PRIORITY ALERT
 * P0 STABILITY UPDATE: High-contrast, sticky banner that cannot be missed
 * Shows prominent banner when local draft differs from server
 * INCLUDES client name for confirmation: "Found changes for [CHEN, Emily]"
 */
export function DraftRecoveryBanner({
  isVisible,
  onRestore,
  onDismiss,
  draft,
}) {
  if (!isVisible || !draft) return null;
  
  const draftTime = formatDraftTime(draft.timestamp);
  const draftSummary = getDraftSummary(draft);
  
  // Get client name from draft metadata (stored during cacheDraftLocally)
  const clientName = draft.clientName || null;
  
  // Build summary text
  const summaryParts = [];
  if (draftSummary.fieldCount > 0) {
    summaryParts.push(`${draftSummary.fieldCount} form fields`);
  }
  if (draftSummary.hasConsent) {
    summaryParts.push('consent selections');
  }
  if (draftSummary.goalsCount > 0) {
    summaryParts.push(`${draftSummary.goalsCount} treatment goal${draftSummary.goalsCount !== 1 ? 's' : ''}`);
  }
  const summaryText = summaryParts.length > 0 ? ` (${summaryParts.join(', ')})` : '';
  
  return (
    // HIGH-CONTRAST: Orange/yellow with thick border, STICKY so it cannot be scrolled past
    <div className="sticky top-0 z-50 bg-gradient-to-r from-orange-100 to-amber-100 border-4 border-orange-400 rounded-lg p-4 mb-4 animate-in slide-in-from-top-2 shadow-lg">
      <div className="flex items-start gap-3">
        {/* Prominent warning icon with pulsing animation */}
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-200 flex-shrink-0 animate-pulse">
          <AlertTriangle className="h-6 w-6 text-orange-700" />
        </div>
        
        <div className="flex-1 min-w-0">
          {/* HIGH VISIBILITY HEADER */}
          <h4 className="text-base font-bold text-orange-900">
            ⚠️ UNSAVED CHANGES RECOVERED
          </h4>
          
          {/* Client name confirmation - THE KEY ADDITION */}
          {clientName && (
            <p className="text-sm font-semibold text-orange-800 mt-1">
              For client: <span className="bg-orange-200 px-2 py-0.5 rounded">{clientName}</span>
            </p>
          )}
          
          <p className="text-sm text-orange-700 mt-1">
            Found changes from <strong className="text-orange-900">{draftTime}</strong>{summaryText}.
          </p>
          <p className="text-xs text-orange-600 mt-1 font-medium">
            These changes were NOT saved to the server. Restore them to avoid losing your work.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onDismiss}
            className="text-orange-800 border-orange-400 hover:text-orange-900 hover:bg-orange-200 h-9 px-3 font-medium"
          >
            <X className="h-4 w-4 mr-1" />
            Discard
          </Button>
          <Button
            size="sm"
            onClick={onRestore}
            className="bg-orange-600 hover:bg-orange-700 h-9 px-4 font-semibold shadow-md"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Restore My Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DraftRecoveryModal;
